/**
 * Invoices and usage statements (mission 9 WP13 T083; sheet 50 callout 13; B.7, A.5; FR-012; OR-14, S22;
 * C-008; contracts/ui-api.md "Sheet 50").
 *
 * - Invoices are the provider's (`invoiceRow`): View invoice leaves through `leaveForProvider`, so the
 *   hosted URL passes `safeProviderUrl` and is never an `href` in the DOM (S22). No Trellis-generated PDF
 *   (C-008). When the read says the provider did not answer, or a URL is refused, `providerUnavailable`
 *   stands in their place.
 * - Statements are Trellis's own (`statementRow`), one per cycle, named by its full cycle start: Download
 *   CSV is `lib/files.ts` `downloadStatement`, a credentialed fetch saved through a blob, never a link to
 *   the API route (OR-14; mission 6 FR-026). A failure shows `statementDownloadFailed`.
 *
 * States: `rows`, `empty` (`zeroListed`), `provider-unavailable`, `download-failed`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { Band } from '@/components/trellis/Band'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { BillingView, Invoice, Statement } from '@/lib/api/billing'
import { downloadStatement } from '@/lib/files'
import { date, money } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { leaveForProvider } from '@/lib/queries'

function InvoiceRow({ invoice, onUnsafe }: { invoice: Invoice; onUnsafe: () => void }) {
  const qc = useQueryClient()
  const id = useId()
  const open = useMutation({ mutationFn: () => leaveForProvider(qc, invoice.hosted_url), retry: 0, onError: onUnsafe })
  const text = PROVISIONAL_COPY.invoiceRow.render({
    period: `${date(invoice.period_start)} – ${date(invoice.period_end)}`,
    amount: money(invoice.amount_cents),
    status: invoice.status,
  })
  return (
    <li className="flex items-center justify-between gap-4">
      <span id={id} className="text-body text-navy-950">
        {text}
      </span>
      <Button variant="secondary" size="sm" aria-describedby={id} loading={open.isPending} onClick={() => open.mutate()}>
        {copyText('invoiceOpen')}
      </Button>
    </li>
  )
}

function StatementRow({ statement }: { statement: Statement }) {
  const id = useId()
  const download = useMutation({ mutationFn: () => downloadStatement(statement.cycle_start), retry: 0 })
  return (
    <li className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <span id={id} className="text-body text-navy-950">
          {PROVISIONAL_COPY.statementRow.render({ date: date(statement.cycle_start) })}
        </span>
        <Button variant="secondary" size="sm" aria-describedby={id} loading={download.isPending} onClick={() => download.mutate()}>
          {copyText('statementDownload')}
        </Button>
      </div>
      {download.isError ? (
        <Band kind="blocked">
          <p>{copyText('statementDownloadFailed')}</p>
        </Band>
      ) : null}
    </li>
  )
}

export function BillingDocuments({ view }: { view: BillingView }) {
  const [unsafe, setUnsafe] = useState(false)
  const unavailable = view.provider_unavailable || unsafe
  const empty = !unavailable && view.invoices.length === 0 && view.statements.length === 0
  return (
    <Card>
      <section className="flex flex-col">
        <CardHeader>
          <CardTitle>{copyText('documentsTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {unavailable ? (
            <Band kind="info">
              <p>{copyText('providerUnavailable')}</p>
            </Band>
          ) : view.invoices.length > 0 ? (
            <ul className="flex flex-col gap-3">
              {view.invoices.map((invoice) => (
                <InvoiceRow key={invoice.id} invoice={invoice} onUnsafe={() => setUnsafe(true)} />
              ))}
            </ul>
          ) : null}
          {view.statements.length > 0 ? (
            <ul className="flex flex-col gap-3">
              {view.statements.map((statement) => (
                <StatementRow key={statement.cycle_start} statement={statement} />
              ))}
            </ul>
          ) : null}
          {empty ? <p className="text-body-sm text-navy-600">{copyText('zeroListed')}</p> : null}
        </CardContent>
      </section>
    </Card>
  )
}
